import { ReviewEntity, ReviewPhotoEntity } from '@/entities/review.entity';
import { ChatMessage } from '@/contracts/marketplace/chat-message';
import { createClientLogger, logClientError, logClientWarning } from '@/client/logging/client-logger';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { toast } from '@/ui/toast';
import type { ReviewAnswerDraftEntity } from '@/contracts/marketplace/auto-replies';

type UseFeedbacksTableModalsArgs = {
    accessToken: string;
};

export type OpenedReview = {
    review: ReviewEntity;
    history: ChatMessage[];
    pendingAnswerDraft: ReviewAnswerDraftEntity | null;
};

export type OpenedPhotos = {
    photos: ReviewPhotoEntity[];
    index: number;
};

const feedbacksTableModalsLogger = createClientLogger({
    scope: 'feedbacks-table-modals',
});

export const useFeedbacksTableModals = ({ accessToken }: UseFeedbacksTableModalsArgs) => {
    const { reviewsApi } = useMarketplaceApi();
    const openedReviewRequestIdRef = useRef(0);

    const [openedReview, setOpenedReview] = useState<OpenedReview | null>(null);
    const [openedPhotos, setOpenedPhotos] = useState<OpenedPhotos | null>(null);
    const [openedAiResponseReview, setOpenedAiResponseReview] = useState<ReviewEntity | null>(null);
    const [isPhotoLoading, setIsPhotoLoading] = useState<boolean>(false);

    const loadPendingAnswerDraft = useCallback(
        async (reviewFeedItemId: string): Promise<ReviewAnswerDraftEntity | null> => {
            try {
                const { data } = await reviewsApi.getAnswerDraft(reviewFeedItemId, { authToken: accessToken });
                return data;
            } catch (error) {
                logClientWarning({
                    logger: feedbacksTableModalsLogger,
                    message: '[client.review-details.answer-draft-load] Failed to load pending answer draft.',
                    error,
                    context: {
                        reviewId: reviewFeedItemId,
                    },
                });
                return null;
            }
        },
        [accessToken, reviewsApi],
    );

    const onRowClick = useCallback(
        async (record: ReviewEntity) => {
            const requestId = openedReviewRequestIdRef.current + 1;
            openedReviewRequestIdRef.current = requestId;
            try {
                const [historyResponse, pendingAnswerDraft] = await Promise.all([
                    reviewsApi.getHistory(record.id, { authToken: accessToken }),
                    loadPendingAnswerDraft(record.id),
                ]);
                if (openedReviewRequestIdRef.current !== requestId) {
                    return;
                }

                setOpenedReview({
                    review: record,
                    history: historyResponse.data,
                    pendingAnswerDraft,
                });
            } catch (error) {
                if (openedReviewRequestIdRef.current !== requestId) {
                    return;
                }

                logClientError({
                    logger: feedbacksTableModalsLogger,
                    message: '[client.review-details.chat-history-load] Failed to load review chat history.',
                    error,
                    context: {
                        reviewId: record.id,
                    },
                });
                toast.warning('Не удалось загрузить диалог');
            }
        },
        [accessToken, loadPendingAnswerDraft, reviewsApi],
    );

    const onPhotoPreviewOpen = useCallback(
        (event: MouseEvent<HTMLButtonElement>, photos: ReviewPhotoEntity[], index: number) => {
            event.preventDefault();
            event.stopPropagation();

            if (!photos.length) return;

            setIsPhotoLoading(true);
            setOpenedPhotos({ photos, index });
        },
        [],
    );

    const onAiResponseOpen = useCallback((event: MouseEvent<HTMLButtonElement>, review: ReviewEntity) => {
        event.preventDefault();
        event.stopPropagation();

        if (!review.aiApiResponse) return;

        setOpenedAiResponseReview(review);
    }, []);

    const onPhotoPreviewClose = useCallback(() => {
        setIsPhotoLoading(false);
        setOpenedPhotos(null);
    }, []);

    const onPreviousPhoto = useCallback(() => {
        setOpenedPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) return prevState;

            setIsPhotoLoading(true);
            const nextIndex = prevState.index === 0 ? prevState.photos.length - 1 : prevState.index - 1;

            return { ...prevState, index: nextIndex };
        });
    }, []);

    const onNextPhoto = useCallback(() => {
        setOpenedPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) return prevState;

            setIsPhotoLoading(true);
            const nextIndex = prevState.index === prevState.photos.length - 1 ? 0 : prevState.index + 1;

            return { ...prevState, index: nextIndex };
        });
    }, []);

    const isPhotoPreviewOpen = !!openedPhotos;

    useEffect(() => {
        if (!isPhotoPreviewOpen) return;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onPhotoPreviewClose();
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onPreviousPhoto();
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                onNextPhoto();
            }
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isPhotoPreviewOpen, onNextPhoto, onPhotoPreviewClose, onPreviousPhoto]);

    return {
        openedReview,
        setOpenedReview,
        openedPhotos,
        isPhotoLoading,
        setIsPhotoLoading,
        openedAiResponseReview,
        setOpenedAiResponseReview,
        onRowClick,
        onPhotoPreviewOpen,
        onAiResponseOpen,
        onPhotoPreviewClose,
        onPreviousPhoto,
        onNextPhoto,
    };
};
